import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import useModal from '@app/component/overlays/modal/ModalRef';
import Modal from '@app/component/overlays/modal/Modal';
import AlertService from '@app/model/alert/service/AlertService';
import Utilities from '@util/Utilities';
import Tag from '@app/component/tag/Tag';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import MarketStateDailySnapshotWithComparisonOutput
	from '@app/model/market_state/output/MarketStateDailySnapshotWithComparisonOutput';
import { MarketStateStatDateCompetitorSnapshot } from '@app/model/market_state/MarketStateStatDateCompetitorSnapshot';
import AlertRuleFilter from '@app/model/alert/AlertRuleFilter';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import MarketStateService from '@app/model/market_state/service/MarketStateService';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import { useQueryClient } from '@tanstack/react-query';
import MarketStateSnapshotCalendarDayTableRow
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/MarketStateSnapshotCalendarDayTableRow';


const MarketStateDetailsModal: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	alertFilter: AlertRuleFilter,
	ref: any
}> = React.forwardRef(({ companyHotel, alertFilter }, ref: React.Ref<any>) => {

	const modal = useModal();
	const queryClient = useQueryClient();
	const filterInput = useMemo<MarketStateFilterInput>(() => ({
		roomTypes: alertFilter.roomTypes,
		refunding: alertFilter.refunding,
		nights: alertFilter.lengthOfStay,
		meal: alertFilter.meal,
		competitorTagIds: [],
	}), [alertFilter]);
	const [marketStateForDay, setMarketStateForDay] = useState<MarketStateDailySnapshotWithComparisonOutput>();
	const [mainHotelMarketState, setMainHotelMarketState] = useState<MarketStateStatDateCompetitorSnapshot>();
	const [competitorSnapshots, setCompetitorSnapshots] = useState<{
		current: MarketStateStatDateCompetitorSnapshot,
		previous: MarketStateStatDateCompetitorSnapshot | undefined
	}[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const findPreviousSnapshot = useCallback(async (statDate: string, snapshotLatestUpdateDate: string) => {
		if (!companyHotel.settings.calendar.numberOfDaysForPriceParity) return undefined;
		const previousSnapshot = await queryClient.fetchQuery({
			queryKey: [...MarketStateGraphQLQueryKeys.previous(companyHotel.id, statDate, filterInput), snapshotLatestUpdateDate],
			staleTime: Infinity,
			queryFn: async () => {
				const snapshot = await MarketStateService.findPreviousMarketStateForDay({
					companyHotelId: companyHotel.id,
					statDate,
					filterInput,
					fromDate: snapshotLatestUpdateDate,
				});
				return snapshot ?? null;
			},
		});
		return previousSnapshot ?? undefined;
	}, [companyHotel.id, companyHotel.settings.calendar.numberOfDaysForPriceParity, filterInput, queryClient]);

	useEffect(() => {
		if (marketStateForDay) {
			setIsLoading(true);
			marketStateForDay.getCompetitorSnapshotsComparison(findPreviousSnapshot)
				.then(snapshots => {
					setCompetitorSnapshots(snapshots);
					setIsLoading(false);
				})
				.catch(error => {
					setIsLoading(false);
				});
		}
	}, [marketStateForDay, findPreviousSnapshot]);

	useImperativeHandle(ref, () => {
		return {
			open: (alertId: number, marketStateId: number) => {
				if (!!companyHotel)
					AlertService.findAlertNotificationMarketState({
						companyHotelId: companyHotel.id,
						alertId: alertId,
						marketStateId: marketStateId,
					}).then((marketStateForDay) => {
						if (!!marketStateForDay) {
							// @ts-ignore
							const snapshot = new MarketStateDailySnapshotWithComparisonOutput({
								current: marketStateForDay,
								previous: undefined,
								statDate: marketStateForDay.statDate,
							});
							setMarketStateForDay(snapshot);
							setMainHotelMarketState(snapshot.current.findDailyMainHotelSnapshot());
						}
					});
				modal.open();
			},
		};
	});


	const comparisonPrice = marketStateForDay?.current.comparisonPrice(companyHotel);

	return <Modal ref={modal.ref}
								title={marketStateForDay ? Utilities.formatDateString(marketStateForDay.statDate, 'DD MMMM YYYY', false) : '--'}
								width="2xlarge">
		{!!marketStateForDay && !!mainHotelMarketState &&
			<div className="inner-wrapper text-left p-4">
				<div>
					<div className="flex items-baseline flex-wrap justify-between gap-y-1 gap-x-4px-4">
						<dt className="text-sm font-medium leading-6 text-gray-500">
							{companyHotel.comparisonLabel()} Compset Rate
						</dt>
						<dd
							className="text-xs font-medium text-gray-300 italic">Updated: {Utilities.formatDateString(marketStateForDay!.current.latestUpdateDate!, 'DD MMMM HH:mm')}</dd>
						<dd className="w-full flex-none text-center">
							<div
								className="text-3xl font-medium tracking-tight text-gray-900 pb">
								{comparisonPrice ? Utilities.formatPrice(comparisonPrice) : companyHotel.usesWeightedAverage() ? 'N/A' : '--'}
							</div>
							<div
								className="block sm:inline-flex items-center ml-0 sm:ml-3 relative bottom-0.5">
								<span className="shrink-0 w-1.5 h-1.5 bg-gray-200 mr-1 rounded-full" />
								<span className="text-xs font-normal text-gray-400">Your difference</span>
								&nbsp;{marketStateForDay.current.evaluatePriceMedian(companyHotel).map((result, idx) =>
								<Tag key={`index_${idx}`}
										 state={result.state}>{result.value}</Tag>)}
							</div>
						</dd>
					</div>
				</div>

				<div className="mt-6">
					<div className="overflow-auto shadow ring-1 ring-black ring-opacity-5 rounded-md">
						<div className="inline-block min-w-full align-middle max-h-[500px]">
							<table className="min-w-full divide-y divide-gray-300">
								<thead className="bg-gray-50">
								<tr>
									<th scope="col"
											className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
										Hotel
									</th>
									<th scope="col"
											className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Price
									</th>
									<th scope="col"
											className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
									</th>
									<th scope="col"
											className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
									</th>
									<th scope="col"
											className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Availability
									</th>
									<th scope="col"
											className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Room Type
									</th>
									<th scope="col"
											className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Provider
									</th>
								</tr>
								</thead>
								<tbody className="divide-y divide-gray-200 bg-white">

								{isLoading ? (
									<tr>
										<td colSpan={7} className="py-8 text-center">
											<div className="flex items-center justify-center space-x-2">
												<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
												<span className="text-sm text-gray-500">Loading competitor data...</span>
											</div>
										</td>
									</tr>
								) : competitorSnapshots.map((item) =>
									<MarketStateSnapshotCalendarDayTableRow
										currentMarketStateForDay={marketStateForDay.current}
										priceRecommendationState={marketStateForDay.priceRecommendationStateSnapshot}
										current={item.current}
										previous={item.previous}
										key={`market-state-hotel-${item.current.competitorId}`} />)}
								</tbody>
							</table>
						</div>
					</div>
				</div>

			</div>}
	</Modal>;
});

export default MarketStateDetailsModal;
